import { FunctionComponent } from 'react';
import { Box, createStyles, makeStyles, Theme, useMediaQuery, useTheme } from '@material-ui/core';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import clsx from 'clsx';
import { useMarketplace } from 'hooks';

export interface Props {
    notificationKey: string;
    content: string;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            backgroundColor: '#FFFFFF',
            borderRadius: '20px',
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            padding: '38px',

            [theme.breakpoints.down('md')]: {
                padding: '10px'
            }
        },
        headerContainer: {
            paddingTop: '20px'
        },
        headerLogo: {
            backgroundColor: '#000000',
            color: '#FFFFFF',
            fontWeight: 500,
            fontSize: '24px',
            padding: '13px 22px',
            width: '212px'
        },
        headerTitle: {
            marginTop: '60px',
            display: 'block',
            fontWeight: 500,
            fontSize: '24px'
        },
        notificationContent: {
            'paddingTop': '32px',
            'marginBottom': '32px',
            'maxWidth': '40vw',
            'wordBreak': 'break-word',
            'overflowWrap': 'break-word',

            '& > p': {
                fontSize: '18px',
                fontWeight: 400,
                margin: '0'
            }
        },
        staticContent: {},
        staticContentHeader: {
            fontSize: '38px',
            fontWeight: 500,
            lineHeight: '110%',
            padding: '21px 0',
            borderTop: '1px solid #ccc',
            borderBottom: '1px solid #ccc',
            marginBottom: '32px',
            display: 'block',

            [theme.breakpoints.down('md')]: {
                fontSize: '30px'
            }
        },
        petsSchedule: {
            paddingBottom: '32px',
            borderBottom: '1px solid #ccc',
            marginBottom: '32px'
        },
        petScheduleName: {
            display: 'block',
            fontSize: '18px',
            lineHeight: '160%'
        },
        petScheduleDate: {
            padding: '10px 13px',
            backgroundColor: '#F8F5F1',
            display: 'block',
            fontSize: '16px',
            fontWeight: 700,
            lineHeight: '100%',
            marginBottom: '8px',
            marginTop: '12px'
        },
        petsScheduleService: {
            display: 'flex',
            gap: 15,
            marginBottom: '8px'
        },
        petsScheduleTime: {
            color: '#7A7A7A',
            fontSize: '16px'
        },
        petsScheduleServiceName: {
            fontSize: '16px'
        },
        petsScheduleAddon: {
            backgroundColor: '#000000',
            color: '#FFFFFF',
            fontSize: '14px',
            fontWeight: 700,
            padding: '3px 5px',
            borderRadius: '25px'
        },
        staticContentFooter: {
            'marginTop': '32px',

            '& > span': {
                display: 'block'
            }
        },
        staticContentFooterHeader: {
            fontSize: '18px',
            fontWeight: 500,
            marginBottom: '24px'
        },
        staticContentFooterTitle: {
            fontWeight: 500
        },
        footerContainer: {
            textAlign: 'center',
            paddingTop: '23px'
        },
        footerLogo: {
            width: '100px',
            [theme.breakpoints.down('sm')]: {
                width: '82px'
            }
        },
        phoneMockupContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignSelf: 'center',
            width: '100%'
        },
        phoneMockup: {
            padding: '24px',
            paddingTop: '60px',
            position: 'relative',
            width: 300,
            height: 600,
            backgroundImage: 'url(/images/customNotificationPreview/mock-phone.svg)',
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            borderRadius: '40px'
        },
        messageContainer: {
            padding: '10px',
            backgroundColor: '#F8F5F1',
            borderRadius: '20px'
        },
        message: {
            fontSize: '14px',
            lineHeight: '119%',
            wordBreak: 'break-word',
            overflowWrap: 'break-word'
        },
        emailNotification: {
            backgroundColor: '#F8F8F8'
        },
        emailNotificationScroll: {
            width: '100%',
            height: '100%',
            overflowY: 'scroll',
            overflowX: 'hidden'
        },
        confirmContainer: {
            marginTop: '32px',
            marginBottom: '50px'
        },
        confirmSection: {
            border: '1px solid #ccc',
            borderRadius: '25px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            margin: '32px 0',
            padding: '24px'
        },
        confirmMessage: {
            fontSize: '24px',
            fontWeight: 500,

            [theme.breakpoints.down('sm')]: {
                fontSize: '20px',
                marginRight: '10px'
            }
        },
        confirmButton: {
            backgroundColor: '#000000',
            borderRadius: '50px',
            color: '#FFFFFF',
            fontSize: '18px',
            fontWeight: 500,
            padding: '10px 32px',

            [theme.breakpoints.down('sm')]: {
                fontSize: '16px',
                whiteSpace: 'nowrap',
                padding: '10px 16px'
            }
        },
        canceledMessage: {
            fontSize: '24px',
            fontWeight: 500,

            [theme.breakpoints.down('sm')]: {
                fontSize: '16px',
                marginRight: '10px'
            }
        },
        canceledButton: {
            backgroundColor: '#000000',
            borderRadius: '50px',
            color: '#FFFFFF',
            fontSize: '18px',
            fontWeight: 500,
            padding: '10px 32px',

            [theme.breakpoints.down('sm')]: {
                fontSize: '12px',
                whiteSpace: 'nowrap',
                padding: '10px 16px'
            }
        },
        appointmentReminderHeader: {
            fontSize: '38px',
            fontWeight: 500,
            lineHeight: '110%',
            padding: '21px 0',
            marginTop: '16px',
            paddingBottom: '0',

            [theme.breakpoints.down('md')]: {
                fontSize: '30px'
            }
        },
        updateVaccineButton: {
            marginBottom: '38px',
            marginTop: '24px'
        },
        thanksMessage: {
            marginBottom: '20px'
        },
        previewLogo: {
            width: '152px',

            [theme.breakpoints.down('sm')]: {
                width: '100px'
            }
        }
    })
);

export const PreviewNotification: FunctionComponent<Props> = props => {
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const marketplace = useMarketplace();

    const previewHeader = (
        <Box className={classes.headerContainer}>
            {marketplace.logos.promoImageSecondaryUrl ? (
                <img
                    className={classes.previewLogo}
                    src={marketplace.logos.promoImageSecondaryUrl}
                />
            ) : (
                <Box className={classes.headerLogo}>Business logo</Box>
            )}

            <span className={classes.headerTitle}>Hi [Client Name]</span>
        </Box>
    );

    const staticFooter = (
        <>
            <Box className={classes.staticContentFooter}>
                <span className={classes.staticContentFooterHeader}>With love,</span>

                <span className={classes.staticContentFooterTitle}>The [Business Name] Team</span>
                <span>(786) 1233-9086</span>
                <span>www.clientsite.com</span>
            </Box>
        </>
    );

    const petsSchedule = (
        <Box className={classes.petsSchedule}>
            <span className={classes.petScheduleName}>
                <strong>Pet Name:</strong> [Pet Name]
            </span>
            <span className={classes.petScheduleDate}>Wednesday, 13 Nov 2024</span>
            <Box className={classes.petsScheduleService}>
                <span className={classes.petsScheduleTime}>10:00am</span>
                <span className={classes.petsScheduleServiceName}>[Service Name]</span>
            </Box>
            <Box className={classes.petsScheduleService}>
                <span className={classes.petsScheduleTime}>10:00am</span>
                <span className={classes.petsScheduleServiceName}>[Service Name]</span>
            </Box>
            <Box className={classes.petsScheduleService}>
                <span className={classes.petsScheduleAddon}>Add-on</span>
                <span className={classes.petsScheduleServiceName}>[Add-on Name]</span>
            </Box>

            <span className={classes.petScheduleDate}>Thursday, 14 Nov 2024</span>

            <Box className={classes.petsScheduleService}>
                <span className={classes.petsScheduleTime}>09:00am</span>
                <span className={classes.petsScheduleServiceName}>[Service Name]</span>
            </Box>
            <Box className={classes.petsScheduleService}>
                <span className={classes.petsScheduleAddon}>Add-on</span>
                <span className={classes.petsScheduleServiceName}>[Add-on Name]</span>
            </Box>
        </Box>
    );

    const postContentMessage = () => {
        switch (props.notificationKey) {
            case 'client_confirmation_request_sms':
                return 'Please confirm here: [URL]';
            case 'client_expiration_vaccine_sms':
                return '[URL]';
            default:
                return '';
        }
    };

    const postContent = postContentMessage();

    const renderNotification = () => {
        if (props.notificationKey.includes('sms')) {
            return (
                <Box className={classes.phoneMockupContainer}>
                    <Box className={classes.phoneMockup}>
                        <Box className={classes.messageContainer}>
                            <span className={classes.message}>
                                {props.content} {postContent}
                            </span>
                        </Box>
                    </Box>
                </Box>
            );
        }

        switch (props.notificationKey) {
            case 'client_new_booking_email':
                return (
                    <Box className={clsx({ [classes.phoneMockupContainer]: isMobile })}>
                        <Box className={clsx(classes.emailNotification, { [classes.phoneMockup]: isMobile })}>
                            <OverlayScrollbarsComponent className={classes.emailNotificationScroll}>
                                <Box className={classes.container}>
                                    {previewHeader}

                                    <Box
                                        className={classes.notificationContent}
                                        dangerouslySetInnerHTML={{ __html: props.content }}
                                    />

                                    <Box className={classes.staticContent}>
                                        <span className={classes.staticContentHeader}>
                                            Here are your
                                            <br />
                                            appointment details
                                        </span>

                                        {petsSchedule}

                                        <span className={classes.petScheduleName}>
                                            <strong>Address:</strong> [Business Address]
                                        </span>

                                        {staticFooter}
                                    </Box>
                                </Box>
                                <Box className={classes.footerContainer}>
                                    <img
                                        className={classes.footerLogo}
                                        src="/images/logo/poweredByCuddles.png"
                                    />
                                </Box>
                            </OverlayScrollbarsComponent>
                        </Box>
                    </Box>
                );

            case 'client_confirmation_request_email':
                return (
                    <Box className={clsx({ [classes.phoneMockupContainer]: isMobile })}>
                        <Box className={clsx(classes.emailNotification, { [classes.phoneMockup]: isMobile })}>
                            <OverlayScrollbarsComponent className={classes.emailNotificationScroll}>
                                <Box className={classes.container}>
                                    {previewHeader}

                                    <Box
                                        className={classes.notificationContent}
                                        dangerouslySetInnerHTML={{ __html: props.content }}
                                    />

                                    <Box className={classes.staticContent}>
                                        {petsSchedule}

                                        <span className={classes.petScheduleName}>
                                            <strong>Address:</strong> [Business Address]
                                        </span>

                                        <div className={classes.confirmContainer}>
                                            <span className={classes.petScheduleName}>
                                                Please confirm by clicking below:
                                            </span>
                                            <div className={classes.confirmSection}>
                                                <span className={classes.confirmMessage}>We’ll be there!</span>
                                                <span className={classes.confirmButton}>Confirm</span>
                                            </div>

                                            <span className={classes.petScheduleName}>
                                                If you need to modify this appointment, please contact us directly.
                                            </span>
                                        </div>

                                        {staticFooter}
                                    </Box>
                                </Box>
                                <Box className={classes.footerContainer}>
                                    <img
                                        className={classes.footerLogo}
                                        src="/images/logo/poweredByCuddles.png"
                                    />
                                </Box>
                            </OverlayScrollbarsComponent>
                        </Box>
                    </Box>
                );

            case 'client_appointment_reminder_email':
                return (
                    <Box className={clsx({ [classes.phoneMockupContainer]: isMobile })}>
                        <Box className={clsx(classes.emailNotification, { [classes.phoneMockup]: isMobile })}>
                            <OverlayScrollbarsComponent className={classes.emailNotificationScroll}>
                                <Box className={classes.container}>
                                    {previewHeader}

                                    <span className={classes.appointmentReminderHeader}>Appointment Reminder</span>

                                    <Box
                                        className={classes.notificationContent}
                                        dangerouslySetInnerHTML={{ __html: props.content }}
                                    />

                                    <Box className={classes.staticContent}>
                                        {petsSchedule}

                                        <span className={classes.petScheduleName}>
                                            <strong>Address:</strong> [Business Address]
                                        </span>

                                        {staticFooter}
                                    </Box>
                                </Box>
                                <Box className={classes.footerContainer}>
                                    <img
                                        className={classes.footerLogo}
                                        src="/images/logo/poweredByCuddles.png"
                                    />
                                </Box>
                            </OverlayScrollbarsComponent>
                        </Box>
                    </Box>
                );

            case 'client_expiration_vaccine_email':
                return (
                    <div className={clsx({ [classes.phoneMockupContainer]: isMobile })}>
                        <div className={clsx(classes.emailNotification, { [classes.phoneMockup]: isMobile })}>
                            <OverlayScrollbarsComponent className={classes.emailNotificationScroll}>
                                <div className={classes.container}>
                                    {previewHeader}

                                    <div
                                        className={classes.notificationContent}
                                        dangerouslySetInnerHTML={{ __html: props.content }}
                                    />

                                    <div className={classes.staticContent}>
                                        <div className={classes.updateVaccineButton}>
                                            <span className={classes.confirmButton}>Update Vaccines</span>
                                        </div>

                                        <div className={classes.thanksMessage}>
                                            <span className={classes.petScheduleName}>
                                                Thank you for taking care of <strong>[Pet Name]!</strong>
                                            </span>
                                        </div>

                                        <div className={classes.thanksMessage}>
                                            <span className={classes.petScheduleName}>See you soon!</span>
                                        </div>

                                        {staticFooter}
                                    </div>
                                </div>
                                <div className={classes.footerContainer}>
                                    <img
                                        className={classes.footerLogo}
                                        src="/images/logo/poweredByCuddles.png"
                                    />
                                </div>
                            </OverlayScrollbarsComponent>
                        </div>
                    </div>
                );
            case 'client_cancelled_appointment_email':
                return (
                    <Box className={clsx({ [classes.phoneMockupContainer]: isMobile })}>
                        <Box className={clsx(classes.emailNotification, { [classes.phoneMockup]: isMobile })}>
                            <OverlayScrollbarsComponent className={classes.emailNotificationScroll}>
                                <Box className={classes.container}>
                                    {previewHeader}

                                    <span className={classes.appointmentReminderHeader}>Appointment Canceled</span>

                                    <Box
                                        className={classes.notificationContent}
                                        dangerouslySetInnerHTML={{ __html: props.content }}
                                    />

                                    <Box className={classes.staticContent}>
                                        {petsSchedule}

                                        <span className={classes.petScheduleName}>
                                            <strong>Address:</strong> [Business Address]
                                        </span>

                                        <div className={classes.confirmContainer}>
                                            <div className={classes.confirmSection}>
                                                <span className={classes.canceledMessage}>Ready to reschedule?</span>
                                                <span className={classes.canceledButton}>Contact us</span>
                                            </div>
                                        </div>

                                        {staticFooter}
                                    </Box>
                                </Box>
                                <Box className={classes.footerContainer}>
                                    <img
                                        className={classes.footerLogo}
                                        src="/images/logo/poweredByCuddles.png"
                                    />
                                </Box>
                            </OverlayScrollbarsComponent>
                        </Box>
                    </Box>
                );

            default:
                return (
                    <Box>
                        <span>
                            No preview available for notification key: <strong>{props.notificationKey}</strong>
                        </span>
                    </Box>
                );
        }
    };

    return renderNotification();
};

export default PreviewNotification;
